/**
 * useProductFilters Hook
 *
 * Encapsulates all filter state for the product FiltersModal.
 * Fetches filter options (catalogues), manages active category,
 * and provides toggle/selectAll/clearAll handlers per category.
 *
 * Filter selections are local — they only sync to the URL (and
 * trigger an API fetch) when `apply()` is called. `reset()` reverts
 * local state back to whatever is currently in the URL.
 *
 * Designed to scale: adding a new filter category means adding
 * its data source and handlers here, not threading 6+ props
 * through the component tree.
 */

// React
import { useState, useCallback, useMemo } from 'react';

// Hooks
import { useCatalogues } from '@hooks/catalogues';

// Types
import type { ApiCatalogue } from '@api/catalogues';
import { PRODUCT_FORM_VALUES } from '@components/Product/types';
import type { FilterCategory, ProductForm } from '@components/Product/types';

export interface ProductFilters {
  // Active tab
  activeCategory: FilterCategory;
  setActiveCategory: (category: FilterCategory) => void;

  // Aggregate counts
  selectedCount: number;
  categoryCounts: Partial<Record<FilterCategory, number>>;
  isLoading: boolean;

  // Footer actions (category-aware)
  selectAll: () => void;
  clearAll: () => void;

  // Deferred apply
  apply: () => void;
  reset: () => void;

  // Catalogues
  catalogues: ApiCatalogue[];
  selectedCatalogueIds: number[];
  toggleCatalogue: (id: number) => void;

  // Diet tags
  selectedDietTags: string[];
  toggleDietTag: (tag: string) => void;

  // Allergen tags
  selectedAllergenTags: string[];
  toggleAllergenTag: (tag: string) => void;

  // Categories
  selectedCategories: string[];
  toggleCategory: (category: string) => void;

  // Nutrition tags
  selectedNutritionTags: string[];
  toggleNutritionTag: (tag: string) => void;

  // Chef's pick
  chefPickSelected: boolean;
  toggleChefPick: () => void;

  // Serving temperature
  selectedServingTemperatures: string[];
  toggleServingTemperature: (temp: string) => void;

  // Status (active/inactive)
  selectedStatuses: string[];
  toggleStatus: (status: string) => void;

  // Format (any-of; empty means every format)
  selectedFormats: ProductForm[];
  toggleFormat: (format: ProductForm) => void;
}

const ALL_DIET_TAGS = ['vegan', 'vegetarian', 'pescatarian', 'spicy'];
const ALL_ALLERGEN_TAGS = ['allium', 'barley', 'dairy', 'egg', 'shellfish', 'soy', 'nuts', 'wheat', 'fish'];
const ALL_NUTRITION_TAGS = ['high_protein', 'low_carbs', 'complex_carbs', 'low_saturated_fat', 'low_cholesterol', 'low_sodium', 'high_fiber', 'low_sugar', 'no_sugar', 'no_caffeine'];
const ALL_SERVING_TEMPERATURES = ['warm', 'chilled'];
export const ALL_STATUSES = ['active', 'inactive'];

/**
 * Selecting every value of a facet and selecting none of them both mean
 * "no restriction", so store the empty form and keep one representation.
 *
 * Only valid for a closed vocabulary. Catalogues, categories and tags come
 * from the server, where picking all of today's values is a genuinely
 * narrower choice than picking none.
 */
function unrestricted<T>(selected: T[], vocabulary: readonly T[]): T[] {
  return vocabulary.every((value) => selected.includes(value)) ? [] : selected;
}

/** A filter's committed value (what the URL holds) and the setter that writes it back. */
interface FilterBinding<T> {
  value: T;
  commit: (next: T) => void;
}

/**
 * Named so a filter cannot be wired to another filter's state. Six of these
 * bindings carry a string[], so a positional list let any two of them swap
 * without a type error.
 */
export interface ProductFiltersInput {
  catalogueIds: FilterBinding<number[]>;
  dietTags: FilterBinding<string[]>;
  allergenTags: FilterBinding<string[]>;
  categories: FilterBinding<string[]>;
  nutritionTags: FilterBinding<string[]>;
  chefPick: FilterBinding<boolean>;
  servingTemperatures: FilterBinding<string[]>;
  statuses: FilterBinding<string[]>;
  formats: FilterBinding<ProductForm[]>;
  // Union of every format's categories, from the server. Replaces the
  // hand-maintained list this file used to export, which had drifted to
  // singular values the backend never accepted.
  allCategories: string[];
}

export function useProductFilters(input: ProductFiltersInput): ProductFilters {
  // Unpacked here, and only here. Every memo and callback below depends on
  // these values, never on `input` — the caller builds a fresh object each
  // render, so depending on it would rebuild them on every keystroke.
  const {
    catalogueIds: { value: committedCatalogueIds, commit: commitCatalogueIds },
    dietTags: { value: committedDietTags, commit: commitDietTags },
    allergenTags: { value: committedAllergenTags, commit: commitAllergenTags },
    categories: { value: committedCategories, commit: commitCategories },
    nutritionTags: { value: committedNutritionTags, commit: commitNutritionTags },
    chefPick: { value: committedChefPick, commit: commitChefPick },
    servingTemperatures: { value: committedServingTemperatures, commit: commitServingTemperatures },
    statuses: { value: committedStatuses, commit: commitStatuses },
    formats: { value: committedFormats, commit: commitFormats },
    allCategories,
  } = input;

  const [activeCategory, setActiveCategory] = useState<FilterCategory>('format');

  // Local (pending) state — toggles modify these, not the URL
  const [catalogueIds, setCatalogueIds] = useState(committedCatalogueIds);
  const [dietTags, setDietTags] = useState(committedDietTags);
  const [allergenTags, setAllergenTags] = useState(committedAllergenTags);
  const [categories, setCategories] = useState(committedCategories);
  const [nutritionTags, setNutritionTags] = useState(committedNutritionTags);
  const [chefPick, setChefPick] = useState(committedChefPick);
  const [servingTemperatures, setServingTemperatures] = useState(committedServingTemperatures);
  const [statuses, setStatuses] = useState(committedStatuses);
  const [formats, setFormats] = useState(committedFormats);

  // Flush local state to URL
  const apply = useCallback(() => {
    commitCatalogueIds(catalogueIds);
    commitDietTags(dietTags);
    commitAllergenTags(allergenTags);
    commitCategories(categories);
    commitNutritionTags(nutritionTags);
    commitChefPick(chefPick);
    commitServingTemperatures(servingTemperatures);
    commitStatuses(unrestricted(statuses, ALL_STATUSES));
    commitFormats(unrestricted(formats, PRODUCT_FORM_VALUES));
  }, [catalogueIds, dietTags, allergenTags, categories, nutritionTags, chefPick, servingTemperatures, statuses, formats, commitCatalogueIds, commitDietTags, commitAllergenTags, commitCategories, commitNutritionTags, commitChefPick, commitServingTemperatures, commitStatuses, commitFormats]);

  // Revert local state to what's in the URL
  const reset = useCallback(() => {
    setCatalogueIds(committedCatalogueIds);
    setDietTags(committedDietTags);
    setAllergenTags(committedAllergenTags);
    setCategories(committedCategories);
    setNutritionTags(committedNutritionTags);
    setChefPick(committedChefPick);
    setServingTemperatures(committedServingTemperatures);
    setStatuses(committedStatuses);
    setFormats(committedFormats);
  }, [committedCatalogueIds, committedDietTags, committedAllergenTags, committedCategories, committedNutritionTags, committedChefPick, committedServingTemperatures, committedStatuses, committedFormats]);

  // Catalogues data
  const { data: cataloguesData, isLoading: isCataloguesLoading } = useCatalogues();
  const catalogues = cataloguesData ?? [];

  // Catalogue handlers
  const toggleCatalogue = useCallback((id: number) => {
    setCatalogueIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }, []);

  const selectAllCatalogues = useCallback(() => {
    setCatalogueIds(catalogues.map((c) => c.id));
  }, [catalogues]);

  const clearAllCatalogues = useCallback(() => {
    setCatalogueIds([]);
  }, []);

  // Diet tag handlers
  const toggleDietTag = useCallback((tag: string) => {
    setDietTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  }, []);

  const selectAllDietTags = useCallback(() => {
    setDietTags(ALL_DIET_TAGS);
  }, []);

  const clearAllDietTags = useCallback(() => {
    setDietTags([]);
  }, []);

  // Allergen tag handlers
  const toggleAllergenTag = useCallback((tag: string) => {
    setAllergenTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  }, []);

  const selectAllAllergenTags = useCallback(() => {
    setAllergenTags(ALL_ALLERGEN_TAGS);
  }, []);

  const clearAllAllergenTags = useCallback(() => {
    setAllergenTags([]);
  }, []);

  // Category handlers
  const toggleCategory = useCallback((category: string) => {
    setCategories((prev) => prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]);
  }, []);

  const selectAllCategories = useCallback(() => {
    setCategories([...allCategories]);
  }, [allCategories]);

  const clearAllCategories = useCallback(() => {
    setCategories([]);
  }, []);

  // Nutrition tag handlers
  const toggleNutritionTag = useCallback((tag: string) => {
    setNutritionTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  }, []);

  const selectAllNutritionTags = useCallback(() => {
    setNutritionTags(ALL_NUTRITION_TAGS);
  }, []);

  const clearAllNutritionTags = useCallback(() => {
    setNutritionTags([]);
  }, []);

  // Chef's pick handler
  const toggleChefPick = useCallback(() => {
    setChefPick((prev) => !prev);
  }, []);

  // Serving temperature handlers
  const toggleServingTemperature = useCallback((temp: string) => {
    setServingTemperatures((prev) => prev.includes(temp) ? prev.filter((t) => t !== temp) : [...prev, temp]);
  }, []);

  const selectAllServingTemperatures = useCallback(() => {
    setServingTemperatures(ALL_SERVING_TEMPERATURES);
  }, []);

  const clearAllServingTemperatures = useCallback(() => {
    setServingTemperatures([]);
  }, []);

  // Status handlers
  const toggleStatus = useCallback((status: string) => {
    setStatuses((prev) => prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]);
  }, []);

  const selectAllStatuses = useCallback(() => {
    setStatuses([...ALL_STATUSES]);
  }, []);

  const clearAllStatuses = useCallback(() => {
    setStatuses([]);
  }, []);

  // Format handlers
  const toggleFormat = useCallback((format: ProductForm) => {
    setFormats((prev) => prev.includes(format) ? prev.filter((f) => f !== format) : [...prev, format]);
  }, []);

  const selectAllFormats = useCallback(() => {
    setFormats([...PRODUCT_FORM_VALUES]);
  }, []);

  const clearAllFormats = useCallback(() => {
    setFormats([]);
  }, []);

  // Category-aware footer actions
  const selectAll = useCallback(() => {
    if (activeCategory === 'format') selectAllFormats();
    if (activeCategory === 'status') selectAllStatuses();
    if (activeCategory === 'catalogues') selectAllCatalogues();
    if (activeCategory === 'categories') selectAllCategories();
    if (activeCategory === 'tags') { selectAllDietTags(); selectAllAllergenTags(); selectAllNutritionTags(); setChefPick(true); selectAllServingTemperatures(); }
  }, [activeCategory, selectAllFormats, selectAllStatuses, selectAllCatalogues, selectAllCategories, selectAllDietTags, selectAllAllergenTags, selectAllNutritionTags, selectAllServingTemperatures]);

  const clearAll = useCallback(() => {
    if (activeCategory === 'format') clearAllFormats();
    if (activeCategory === 'status') clearAllStatuses();
    if (activeCategory === 'catalogues') clearAllCatalogues();
    if (activeCategory === 'categories') clearAllCategories();
    if (activeCategory === 'tags') { clearAllDietTags(); clearAllAllergenTags(); clearAllNutritionTags(); setChefPick(false); clearAllServingTemperatures(); }
  }, [activeCategory, clearAllFormats, clearAllStatuses, clearAllCatalogues, clearAllCategories, clearAllDietTags, clearAllAllergenTags, clearAllNutritionTags, clearAllServingTemperatures]);

  // Aggregate counts
  const tagCount = dietTags.length + allergenTags.length + nutritionTags.length + (chefPick ? 1 : 0) + servingTemperatures.length;
  const categoryCounts = useMemo<Partial<Record<FilterCategory, number>>>(() => ({
    format: formats.length || undefined,
    status: statuses.length || undefined,
    catalogues: catalogueIds.length || undefined,
    categories: categories.length || undefined,
    tags: tagCount || undefined,
  }), [formats.length, statuses.length, catalogueIds.length, categories.length, tagCount]);

  const selectedCount = formats.length + statuses.length + catalogueIds.length + categories.length + tagCount;

  return {
    activeCategory,
    setActiveCategory,
    selectedCount,
    categoryCounts,
    isLoading: isCataloguesLoading,
    selectAll,
    clearAll,
    apply,
    reset,
    catalogues,
    selectedCatalogueIds: catalogueIds,
    toggleCatalogue,
    selectedDietTags: dietTags,
    toggleDietTag,
    selectedAllergenTags: allergenTags,
    toggleAllergenTag,
    selectedCategories: categories,
    toggleCategory,
    selectedNutritionTags: nutritionTags,
    toggleNutritionTag,
    chefPickSelected: chefPick,
    toggleChefPick,
    selectedServingTemperatures: servingTemperatures,
    toggleServingTemperature,
    selectedStatuses: statuses,
    toggleStatus,
    selectedFormats: formats,
    toggleFormat,
  };
}
